import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Sparkles, Star, Wind, PawPrint, Apple, BookOpen, ChevronRight, Heart, Play, Pause, Loader2, Crown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { READABLES_LIBRARY } from "../data/readablesData";
import { toast } from "sonner";
import axios from "axios";
import { getAudio as getCachedAudio, saveAudio as saveCachedAudio, TODAY_KEY } from "../utils/audioCache";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ForYouFeed — Personalized daily recommendations based on:
 *  - Time of day (morning/afternoon/evening)
 *  - User's quiz answers (spiritual focus, dietary preference)
 *  - Most recent vibe check mood
 *  - Their personal spirit animal / life path
 *
 * All picks are deterministic per user per day so the feed feels steady,
 * not chaotic — but it still refreshes every 24 hours.
 */
const ForYouFeed = () => {
  const { user, getAuthHeader } = useAuth();
  const [picks, setPicks] = useState(null);
  const [audioState, setAudioState] = useState("idle"); // idle | loading | playing | paused
  const [autoplayBlocked, setAutoplayBlocked] = useState(false); // true when browser refused auto-play
  const audioRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    fetchPersonalizedPicks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // The browser's getTimezoneOffset() is flipped (returns -offset_minutes),
  // so negate once here for clarity. Sent to the backend so greetings and the
  // cached audio track the user's LOCAL hour, not UTC.
  const tzOffsetMinutes = -new Date().getTimezoneOffset();

  const fetchPersonalizedPicks = async () => {
    try {
      const res = await axios.get(
        `${API}/api/for-you?tz_offset_minutes=${tzOffsetMinutes}`,
        { headers: getAuthHeader() },
      );
      // Override book pick to use OUR Readables library (not external books).
      // Exclude any "Life Path" books — those belong only in the user's
      // Cosmic Blueprint, where their OWN number lives. Recommending a
      // different life-path number here just confuses people.
      const eligibleBooks = (READABLES_LIBRARY || []).filter((b) => {
        const cat = (b?.category || "").toLowerCase();
        const cats = (b?.categories || []).map((c) => (c || "").toLowerCase());
        const titleLc = (b?.title || "").toLowerCase();
        if (cat === "life path") return false;
        if (cats.includes("life path")) return false;
        if (titleLc.startsWith("life path ")) return false;
        return true;
      });
      const data = { ...res.data };
      if (eligibleBooks.length > 0) {
        // Deterministic daily pick from our library (same seed approach as backend)
        const dateKey = new Date().toISOString().slice(0, 10);
        const seedStr = (user?.id || "anon") + "-" + dateKey;
        let hash = 0;
        for (let i = 0; i < seedStr.length; i++) {
          hash = ((hash << 5) - hash) + seedStr.charCodeAt(i);
          hash |= 0;
        }
        const idx = Math.abs(hash) % eligibleBooks.length;
        const b = eligibleBooks[idx];
        data.book = {
          id: b.id,
          title: b.title,
          author: b.subtitle || "Lala Land Readables",
          cover: b.cover,
        };
      }
      setPicks(data);
    } catch (err) {
      // Silent fallback — feed is supplemental, shouldn't block dashboard
      console.debug("ForYou feed unavailable:", err.message);
    }
  };

  const handleTogglePlay = async ({ fromAutoplay = false } = {}) => {
    if (!user?.is_premium) {
      toast("Premium feature", {
        description: "For You audio narration is part of your Rooted or Forever Rooted plan.",
      });
      return;
    }

    // Pause/resume existing audio
    if (audioRef.current) {
      if (audioState === "playing") {
        audioRef.current.pause();
        setAudioState("paused");
      } else if (audioState === "paused") {
        try { await audioRef.current.play(); setAudioState("playing"); } catch { /* ignored */ }
      }
      return;
    }

    // Generate fresh audio — try IDB cache first (7-day offline window).
    setAudioState("loading");
    try {
      let url = null;

      // 1. Hit IndexedDB first. Cache key is scoped by the current time-of-day
      //    bucket (morning/afternoon/evening) so a "good morning" audio cached
      //    this morning never plays in the evening. `picks.time_of_day` comes
      //    from the backend and already respects the user's local tz offset.
      const todBucket = picks?.time_of_day || "today";
      const cacheKey = `${TODAY_KEY()}-${todBucket}`;
      if (user?.id) {
        const cached = await getCachedAudio(user.id, cacheKey);
        if (cached?.audio_url) {
          url = cached.audio_url;
        }
      }

      // 2. Cache miss → hit the backend (which has its own Mongo cache).
      if (!url) {
        const res = await axios.post(
          `${API}/api/for-you/audio`,
          { tz_offset_minutes: tzOffsetMinutes },
          { headers: getAuthHeader() },
        );
        url = res.data.audio_url;
        // Persist to IDB so next session (even offline) hears it instantly.
        // Fire-and-forget — playback shouldn't wait on storage.
        if (user?.id) {
          saveCachedAudio({
            user_id: user.id,
            date: cacheKey,
            audio_url: url,
            script: res.data.script,
            voice: res.data.voice,
            provider: res.data.provider,
          }).catch(() => {});
        }
      }

      const audio = new Audio(url);
      audio.onended = () => { setAudioState("idle"); audioRef.current = null; };
      audio.onerror = () => { setAudioState("idle"); audioRef.current = null; toast.error("Audio playback failed"); };
      audioRef.current = audio;
      try {
        await audio.play();
        setAudioState("playing");
        setAutoplayBlocked(false);
      } catch (playErr) {
        // Browser autoplay policy blocked us (no user gesture yet). Keep the audio
        // ready and surface a pulsing "Tap to listen" CTA so the next tap starts it.
        if (fromAutoplay) {
          setAutoplayBlocked(true);
          setAudioState("paused");
          toast("Tap the pulsing button to hear your Daily Whisper", { duration: 5000 });
        } else {
          setAudioState("idle");
          audioRef.current = null;
          toast.error("Couldn't play audio. Please try again.");
        }
      }
    } catch (err) {
      setAudioState("idle");
      // When offline with no cache, surface a gentle offline toast instead of
      // the generic "couldn't generate" message.
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        toast("Today's narration isn't cached yet — tap Listen once you're back online and we'll save it for tomorrow 🌱");
      } else {
        const msg = err?.response?.data?.detail || "Couldn't generate audio right now";
        toast.error(msg);
      }
    }
  };

  // Clean up audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Daily Whisper deep-link: once picks are loaded, scroll the For You card into
  // view if the URL has #for-you, and auto-start narration if ?autoplay=foryou.
  // This makes the 7 AM push feel magical — tap the notification and the app
  // opens right at the audio with playback already starting.
  const hasAutoPlayedRef = useRef(false);
  useEffect(() => {
    if (!picks) return;
    const params = new URLSearchParams(window.location.search);
    const wantsScroll = window.location.hash === "#for-you" || params.get("scrollTo") === "for-you";
    const wantsAutoplay = params.get("autoplay") === "foryou";

    if (wantsScroll) {
      requestAnimationFrame(() => {
        const el = document.getElementById("for-you");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
    if (wantsAutoplay && user?.is_premium && !hasAutoPlayedRef.current && audioState === "idle") {
      hasAutoPlayedRef.current = true;
      // Small delay lets the scroll settle before the TTS fetch spinner appears.
      // Note: handleTogglePlay handles autoplay-policy rejection gracefully by
      // showing a pulsing "Tap to listen" CTA (autoplayBlocked=true).
      const t = setTimeout(() => { handleTogglePlay({ fromAutoplay: true }); }, 600);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks]);

  if (!picks) return null;

  const timeOfDay = picks.time_of_day || "today";
  const greeting = timeOfDay === "morning" ? "Good morning" : timeOfDay === "afternoon" ? "This afternoon" : "This evening";

  return (
    <Card className="border-accent/30 bg-gradient-to-br from-accent/5 via-primary/5 to-violet-500/5 overflow-hidden" data-testid="for-you-feed" id="for-you">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-violet-500 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-primary text-sm sm:text-base">For You, {user?.name?.split(" ")[0] || "Beautiful Soul"}</h3>
            <p className="text-[11px] text-muted-foreground">{greeting}, here's what feels aligned</p>
          </div>
          <button
            type="button"
            onClick={() => handleTogglePlay()}
            disabled={audioState === "loading"}
            aria-label={audioState === "playing" ? "Pause For You audio" : "Play For You audio"}
            data-testid="for-you-audio-toggle"
            className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all shadow-sm ${
              audioState === "playing"
                ? "bg-gradient-to-r from-accent to-violet-500 text-white"
                : "bg-background/70 border border-accent/30 text-accent hover:bg-accent/10"
            } ${audioState === "loading" ? "opacity-70 cursor-wait" : "hover:scale-105"} ${
              autoplayBlocked && audioState !== "playing" ? "ring-2 ring-accent/60 ring-offset-1 ring-offset-background animate-pulse" : ""
            }`}
          >
            {audioState === "loading" ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="hidden sm:inline">Preparing…</span>
              </>
            ) : audioState === "playing" ? (
              <>
                <Pause className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Pause</span>
              </>
            ) : audioState === "paused" ? (
              <>
                <Play className="h-3.5 w-3.5 fill-current" />
                <span className="hidden sm:inline">Resume</span>
              </>
            ) : (
              <>
                <Play className="h-3.5 w-3.5 fill-current" />
                <span className="hidden sm:inline">Listen</span>
                {!user?.is_premium && <Crown className="h-3 w-3 text-amber-400 ml-0.5" />}
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {picks.affirmation && (
            <Link to="/affirmations" className="group" data-testid="for-you-affirmation">
              <div className="rounded-xl p-3 bg-background/60 border border-border/40 hover:border-accent/40 transition h-full flex flex-col">
                <div className="flex items-center gap-2 mb-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-accent" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Affirmation</span>
                </div>
                <p className="text-sm text-primary italic leading-snug line-clamp-3 flex-1">"{picks.affirmation.text}"</p>
                <div className="text-[11px] text-accent mt-2 flex items-center gap-0.5 group-hover:gap-1.5 transition-all">
                  Speak it over yourself <ChevronRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          )}

          {picks.angel_number && (
            <Link to={`/angel-numbers${picks.angel_number.slug ? `?number=${picks.angel_number.slug}` : ""}`} className="group" data-testid="for-you-angel-number">
              <div className="rounded-xl p-3 bg-background/60 border border-border/40 hover:border-accent/40 transition h-full flex flex-col">
                <div className="flex items-center gap-2 mb-1.5">
                  <Star className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Today's Angel Number</span>
                </div>
                <p className="text-2xl font-bold text-amber-600 mb-1">{picks.angel_number.number}</p>
                <p className="text-xs text-muted-foreground line-clamp-2 flex-1">{picks.angel_number.meaning}</p>
              </div>
            </Link>
          )}

          {picks.meditation && (
            <Link
              to={picks.meditation.id ? `/guided-meditations?autoplay=${picks.meditation.id}` : "/guided-meditations"}
              className="group"
              data-testid="for-you-meditation"
            >
              <div className="rounded-xl p-3 bg-background/60 border border-border/40 hover:border-cyan-500/40 transition h-full flex flex-col">
                <div className="flex items-center gap-2 mb-1.5">
                  <Wind className="h-3.5 w-3.5 text-cyan-500" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Meditation</span>
                </div>
                <p className="text-sm font-medium text-primary leading-snug">{picks.meditation.title}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2 flex-1">
                  {picks.meditation.duration || "10 min"} · {picks.meditation.description || picks.meditation.reason || "Take a mindful pause."}
                </p>
              </div>
            </Link>
          )}

          {picks.spirit_animal && (
            <Link
              to={picks.spirit_animal.name ? `/spirit-animals?animal=${encodeURIComponent(picks.spirit_animal.name.toLowerCase())}` : "/spirit-animals"}
              className="group"
              data-testid="for-you-spirit-animal"
            >
              <div className="rounded-xl p-3 bg-background/60 border border-border/40 hover:border-emerald-500/40 transition h-full flex flex-col">
                <div className="flex items-center gap-2 mb-1.5">
                  <PawPrint className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Your Spirit Guide</span>
                </div>
                <p className="text-sm font-medium text-primary">{picks.spirit_animal.name}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2 flex-1">{picks.spirit_animal.message}</p>
              </div>
            </Link>
          )}

          {picks.recipe && (
            <Link
              to={picks.recipe.id ? `/nutrition?tab=recipes&item=${picks.recipe.id}&name=${encodeURIComponent(picks.recipe.name || "")}` : "/nutrition"}
              className="group"
              data-testid="for-you-recipe"
            >
              <div className="rounded-xl p-3 bg-background/60 border border-border/40 hover:border-green-500/40 transition h-full flex flex-col">
                <div className="flex items-center gap-2 mb-1.5">
                  <Apple className="h-3.5 w-3.5 text-green-500" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Nourish</span>
                </div>
                <p className="text-sm font-medium text-primary">{picks.recipe.name}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2 flex-1">
                  {picks.recipe.description || picks.recipe.reason || `${picks.recipe.meal_type || "meal"} · ${picks.recipe.prep_time || ""}`.trim()}
                </p>
              </div>
            </Link>
          )}

          {picks.book && (
            <Link to={`/readables${picks.book.id ? `?book=${picks.book.id}` : ""}`} className="group" data-testid="for-you-book">
              <div className="rounded-xl p-3 bg-background/60 border border-border/40 hover:border-violet-500/40 transition h-full flex flex-col">
                <div className="flex items-center gap-2 mb-1.5">
                  <BookOpen className="h-3.5 w-3.5 text-violet-500" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">From Our Readables</span>
                </div>
                <div className="flex items-start gap-2 flex-1">
                  {picks.book.cover && <span className="text-2xl flex-shrink-0 leading-none">{picks.book.cover}</span>}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary leading-snug break-words">{picks.book.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 break-words line-clamp-2">{picks.book.author}</p>
                  </div>
                </div>
              </div>
            </Link>
          )}
        </div>

        <div className="mt-4 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
          <Heart className="h-3 w-3 text-accent" />
          <span>Picked just for you · refreshes daily</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default ForYouFeed;
